/**
 * PhoenixBilling Public Landing Page
 * The first thing customers see - a bold, dark-mode gaming/tech aesthetic
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Server,
  Shield,
  Globe,
  HeadphonesIcon,
  Users,
  BarChart3,
  Zap,
  ArrowRight,
  Menu,
  X,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Product {
  name: string;
  description: string;
  startingPrice?: number;
  pricing?: { monthly?: number };
}

interface ProductGroup {
  name: string;
  products: Product[];
}

interface ApiResponse {
  success: boolean;
  data: {
    groups: ProductGroup[];
  };
}

/* ------------------------------------------------------------------ */
/*  Intersection Observer hook for scroll-triggered animations         */
/* ------------------------------------------------------------------ */

function useAnimateOnScroll<T extends HTMLElement>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

/* ------------------------------------------------------------------ */
/*  Feature data                                                       */
/* ------------------------------------------------------------------ */

const features = [
  {
    icon: Server,
    title: 'Automated Provisioning',
    description:
      'Instantly deploy servers, game panels, and hosting accounts the moment a payment clears. Zero manual intervention required.',
  },
  {
    icon: Shield,
    title: 'Invoice Management',
    description:
      'Professional invoices with automated reminders, partial payments, credit notes, and multi-currency support built-in.',
  },
  {
    icon: Globe,
    title: 'Domain Registration',
    description:
      'Full domain lifecycle management with WHOIS, DNS, EPP transfers, and auto-renewal across all major registrars.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Support Tickets',
    description:
      'Departmentalized ticket system with canned responses, SLA tracking, and internal notes to keep your team efficient.',
  },
  {
    icon: Users,
    title: 'Client Portal',
    description:
      'A polished self-service portal where clients manage services, pay invoices, open tickets, and track orders.',
  },
  {
    icon: BarChart3,
    title: 'Analytics Dashboard',
    description:
      'Real-time revenue, churn, MRR, and customer analytics give you the insights to grow your hosting business.',
  },
];

/* ------------------------------------------------------------------ */
/*  Placeholder products (fallback if API fails)                       */
/* ------------------------------------------------------------------ */

const placeholderProducts: Product[] = [
  {
    name: 'Starter',
    description: 'Perfect for new hosting businesses getting off the ground.',
    pricing: { monthly: 9.99 },
  },
  {
    name: 'Professional',
    description: 'For growing providers who need advanced automation and analytics.',
    pricing: { monthly: 24.99 },
  },
  {
    name: 'Enterprise',
    description: 'Unlimited scale with priority support and custom integrations.',
    pricing: { monthly: 49.99 },
  },
];

/* ------------------------------------------------------------------ */
/*  Navbar                                                             */
/* ------------------------------------------------------------------ */

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50 shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 shadow-glow group-hover:shadow-glow-lg transition-shadow duration-300">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-display tracking-wider text-white">
              Phoenix<span className="text-orange-500">Billing</span>
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            <a
              href="#features"
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors duration-200"
            >
              Features
            </a>
            <a
              href="#pricing"
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors duration-200"
            >
              Pricing
            </a>
            <Link
              to="/knowledge-base"
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors duration-200"
            >
              Knowledge Base
            </Link>
            <Link
              to="/network-status"
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors duration-200"
            >
              Network Status
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="px-5 py-2 text-sm font-medium text-slate-300 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition-all duration-200"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-lg shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300"
            >
              Register
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Toggle navigation menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          open ? 'max-h-96 border-b border-slate-700/50' : 'max-h-0'
        }`}
      >
        <div className="px-4 py-4 bg-slate-900/95 backdrop-blur-xl space-y-1">
          <a
            href="#features"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors"
          >
            Features
          </a>
          <a
            href="#pricing"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors"
          >
            Pricing
          </a>
          <Link
            to="/knowledge-base"
            className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors"
          >
            Knowledge Base
          </Link>
          <Link
            to="/network-status"
            className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/60 transition-colors"
          >
            Network Status
          </Link>
          <div className="pt-3 border-t border-slate-700/50 flex gap-3">
            <Link
              to="/login"
              className="flex-1 text-center px-4 py-2.5 text-sm font-medium text-slate-300 border border-slate-600 rounded-lg hover:text-white hover:border-slate-500 transition-colors"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="flex-1 text-center px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg shadow-orange-500/25 transition-all"
            >
              Register
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */

const Hero: React.FC = () => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
    {/* Animated background orbs */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Grid overlay */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.03]" />

      {/* Large orange orb - top left */}
      <div
        className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(255,107,53,0.4) 0%, transparent 70%)',
          animation: 'heroOrb1 8s ease-in-out infinite',
        }}
      />
      {/* Purple orb - top right */}
      <div
        className="absolute -top-20 -right-40 w-[500px] h-[500px] rounded-full opacity-15"
        style={{
          background: 'radial-gradient(circle, rgba(168,85,247,0.4) 0%, transparent 70%)',
          animation: 'heroOrb2 10s ease-in-out infinite',
        }}
      />
      {/* Cyan orb - bottom */}
      <div
        className="absolute -bottom-40 left-1/3 w-[500px] h-[500px] rounded-full opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(6,182,212,0.35) 0%, transparent 70%)',
          animation: 'heroOrb3 12s ease-in-out infinite',
        }}
      />
      {/* Small bright accent */}
      <div
        className="absolute top-1/3 right-1/4 w-[200px] h-[200px] rounded-full opacity-25"
        style={{
          background: 'radial-gradient(circle, rgba(255,107,53,0.5) 0%, transparent 70%)',
          animation: 'heroOrb4 6s ease-in-out infinite',
        }}
      />
    </div>

    {/* Keyframes injected via <style> */}
    <style>{`
      @keyframes heroOrb1 {
        0%, 100% { transform: translate(0, 0) scale(1); }
        33% { transform: translate(60px, 40px) scale(1.05); }
        66% { transform: translate(-30px, 60px) scale(0.95); }
      }
      @keyframes heroOrb2 {
        0%, 100% { transform: translate(0, 0) scale(1); }
        33% { transform: translate(-50px, 50px) scale(1.08); }
        66% { transform: translate(40px, -30px) scale(0.96); }
      }
      @keyframes heroOrb3 {
        0%, 100% { transform: translate(0, 0) scale(1); }
        50% { transform: translate(80px, -60px) scale(1.1); }
      }
      @keyframes heroOrb4 {
        0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.25; }
        50% { transform: translate(-40px, 30px) scale(1.2); opacity: 0.4; }
      }
      @keyframes heroFadeUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes heroFadeUpDelay1 {
        0%, 15% { opacity: 0; transform: translateY(30px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes heroFadeUpDelay2 {
        0%, 30% { opacity: 0; transform: translateY(30px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes heroFadeUpDelay3 {
        0%, 45% { opacity: 0; transform: translateY(30px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes badgePulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(255,107,53,0.4); }
        50% { box-shadow: 0 0 0 8px rgba(255,107,53,0); }
      }
    `}</style>

    <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
      {/* Badge */}
      <div
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 mb-8"
        style={{ animation: 'heroFadeUp 0.8s ease-out both' }}
      >
        <span
          className="w-2 h-2 rounded-full bg-orange-500"
          style={{ animation: 'badgePulse 2s ease-in-out infinite' }}
        />
        <span className="text-sm font-medium text-orange-400 tracking-wide">
          The Modern WHMCS Alternative
        </span>
      </div>

      {/* Headline */}
      <h1
        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold font-display tracking-tight leading-[1.1]"
        style={{ animation: 'heroFadeUpDelay1 1s ease-out both' }}
      >
        <span className="text-white">Next-Gen</span>
        <br />
        <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent">
          Billing Platform
        </span>
      </h1>

      {/* Subheadline */}
      <p
        className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl text-slate-400 leading-relaxed"
        style={{ animation: 'heroFadeUpDelay2 1.1s ease-out both' }}
      >
        Power your hosting business with automated provisioning, beautiful invoicing,
        and a client portal your customers will love.
        Built for speed, designed for scale.
      </p>

      {/* CTAs */}
      <div
        className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        style={{ animation: 'heroFadeUpDelay3 1.2s ease-out both' }}
      >
        <Link
          to="/register"
          className="group relative inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
        >
          Get Started
          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <a
          href="#pricing"
          className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-slate-300 hover:text-white border border-slate-600 hover:border-slate-500 rounded-xl hover:bg-slate-800/50 transition-all duration-300 hover:-translate-y-0.5"
        >
          View Plans
        </a>
      </div>

      {/* Trust bar */}
      <div
        className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-slate-500"
        style={{ animation: 'heroFadeUpDelay3 1.4s ease-out both' }}
      >
        <span className="flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5 text-orange-500/70" />
          SSL Encrypted
        </span>
        <span className="flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5 text-orange-500/70" />
          99.9% Uptime
        </span>
        <span className="flex items-center gap-1.5">
          <HeadphonesIcon className="w-3.5 h-3.5 text-orange-500/70" />
          24/7 Support
        </span>
        <span className="flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5 text-orange-500/70" />
          Global Infrastructure
        </span>
      </div>
    </div>

    {/* Bottom fade into next section */}
    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none" />
  </section>
);

/* ------------------------------------------------------------------ */
/*  Features                                                           */
/* ------------------------------------------------------------------ */

const FeatureCard: React.FC<{
  icon: React.ElementType;
  title: string;
  description: string;
  index: number;
}> = ({ icon: Icon, title, description, index }) => {
  const [ref, visible] = useAnimateOnScroll<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`group relative p-6 sm:p-8 rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-orange-500/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/5 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        transitionDelay: visible ? `${index * 100}ms` : '0ms',
        transitionProperty: 'opacity, transform, border-color, box-shadow',
      }}
    >
      {/* Subtle gradient hover glow */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="relative">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/20 mb-5 group-hover:shadow-glow transition-shadow duration-500">
          <Icon className="w-6 h-6 text-orange-400" />
        </div>
        <h3 className="text-lg font-bold text-white mb-2 font-display tracking-wide">
          {title}
        </h3>
        <p className="text-sm leading-relaxed text-slate-400 group-hover:text-slate-300 transition-colors duration-300">
          {description}
        </p>
      </div>
    </div>
  );
};

const Features: React.FC = () => {
  const [headingRef, headingVisible] = useAnimateOnScroll<HTMLDivElement>();

  return (
    <section id="features" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div
          ref={headingRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-3 py-1 text-xs font-semibold tracking-widest uppercase text-orange-400 bg-orange-500/10 rounded-full border border-orange-500/20 mb-4">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight text-white">
            Everything You Need to{' '}
            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              Run Your Business
            </span>
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            A complete billing and automation platform purpose-built for hosting providers,
            game server companies, and SaaS businesses.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <FeatureCard key={f.title} index={i} {...f} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Products / Pricing                                                 */
/* ------------------------------------------------------------------ */

const ProductCard: React.FC<{
  product: Product;
  index: number;
  featured?: boolean;
}> = ({ product, index, featured }) => {
  const [ref, visible] = useAnimateOnScroll<HTMLDivElement>();
  const price = product.pricing?.monthly ?? product.startingPrice ?? null;

  return (
    <div
      ref={ref}
      className={`relative flex flex-col rounded-2xl transition-all duration-500 hover:-translate-y-1 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${
        featured
          ? 'bg-gradient-to-b from-orange-500/10 to-slate-800/80 border-2 border-orange-500/40 shadow-xl shadow-orange-500/10'
          : 'bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-orange-500/30'
      }`}
      style={{
        transitionDelay: visible ? `${index * 120}ms` : '0ms',
        transitionProperty: 'opacity, transform, border-color, box-shadow',
      }}
    >
      {/* "Popular" badge */}
      {featured && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center px-4 py-1 text-xs font-bold tracking-wider uppercase text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-full shadow-lg shadow-orange-500/30">
            Most Popular
          </span>
        </div>
      )}

      <div className="flex-1 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-white font-display tracking-wide">
          {product.name}
        </h3>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed min-h-[3rem]">
          {product.description}
        </p>

        <div className="mt-6 flex items-baseline gap-1">
          {price !== null ? (
            <>
              <span className="text-4xl font-extrabold text-white font-display">
                ${price.toFixed(2)}
              </span>
              <span className="text-sm text-slate-500">/mo</span>
            </>
          ) : (
            <span className="text-2xl font-bold text-white font-display">Contact Us</span>
          )}
        </div>
      </div>

      <div className="p-6 sm:p-8 pt-0">
        <Link
          to="/register"
          className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
            featured
              ? 'text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40'
              : 'text-orange-400 border border-orange-500/30 hover:bg-orange-500/10 hover:border-orange-500/50'
          }`}
        >
          Order Now
        </Link>
      </div>
    </div>
  );
};

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [headingRef, headingVisible] = useAnimateOnScroll<HTMLDivElement>();

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${import.meta.env.VITE_API_URL || '/api/v1'}/public/products`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json() as Promise<ApiResponse>;
      })
      .then((data) => {
        if (data.success && data.data?.groups?.length) {
          const all = data.data.groups.flatMap((g: ProductGroup) => g.products);
          setProducts(all.length > 0 ? all : placeholderProducts);
        } else {
          setProducts(placeholderProducts);
        }
      })
      .catch(() => {
        setError(true);
        setProducts(placeholderProducts);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, []);

  return (
    <section id="pricing" className="relative py-24 sm:py-32">
      {/* Subtle top divider glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div
          ref={headingRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-3 py-1 text-xs font-semibold tracking-widest uppercase text-orange-400 bg-orange-500/10 rounded-full border border-orange-500/20 mb-4">
            Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight text-white">
            Simple,{' '}
            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              Transparent
            </span>{' '}
            Pricing
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            Pick a plan that fits your scale. Upgrade or downgrade at any time.
          </p>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl bg-slate-800/50 border border-slate-700/50 p-8 animate-pulse"
              >
                <div className="h-6 w-24 bg-slate-700/60 rounded mb-3" />
                <div className="h-4 w-full bg-slate-700/40 rounded mb-2" />
                <div className="h-4 w-2/3 bg-slate-700/40 rounded mb-8" />
                <div className="h-10 w-32 bg-slate-700/60 rounded mb-8" />
                <div className="h-12 w-full bg-slate-700/40 rounded-xl" />
              </div>
            ))}
          </div>
        )}

        {/* Product cards */}
        {!loading && (
          <div
            className={`grid gap-6 ${
              products.length === 1
                ? 'grid-cols-1 max-w-md mx-auto'
                : products.length === 2
                ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto'
                : products.length >= 4
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
                : 'grid-cols-1 md:grid-cols-3'
            }`}
          >
            {products.map((product, i) => (
              <ProductCard
                key={`${product.name}-${i}`}
                product={product}
                index={i}
                featured={
                  products.length >= 3
                    ? i === Math.floor(products.length / 2)
                    : i === 0
                }
              />
            ))}
          </div>
        )}

        {/* Error notice */}
        {error && !loading && (
          <p className="mt-6 text-center text-sm text-slate-500">
            Showing sample pricing. Live plans will appear once the API is available.
          </p>
        )}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  CTA Banner                                                         */
/* ------------------------------------------------------------------ */

const CtaBanner: React.FC = () => {
  const [ref, visible] = useAnimateOnScroll<HTMLDivElement>();

  return (
    <section className="relative py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700/50 p-10 sm:p-16 text-center transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Background decoration */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div
              className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-20"
              style={{
                background: 'radial-gradient(circle, rgba(255,107,53,0.5) 0%, transparent 70%)',
              }}
            />
            <div
              className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full opacity-10"
              style={{
                background: 'radial-gradient(circle, rgba(168,85,247,0.4) 0%, transparent 70%)',
              }}
            />
          </div>

          <div className="relative z-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold font-display tracking-tight text-white">
              Ready to{' '}
              <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                Transform
              </span>{' '}
              Your Business?
            </h2>
            <p className="mt-4 max-w-xl mx-auto text-lg text-slate-400">
              Join hundreds of hosting providers who have already switched to PhoenixBilling.
              Get started in minutes, not days.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="group inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-slate-300 hover:text-white border border-slate-600 hover:border-slate-500 rounded-xl hover:bg-slate-800/50 transition-all duration-300"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Footer                                                             */
/* ------------------------------------------------------------------ */

const Footer: React.FC = () => (
  <footer className="border-t border-slate-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand column */}
        <div className="sm:col-span-2 lg:col-span-1">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 shadow-glow group-hover:shadow-glow-lg transition-shadow duration-300">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold font-display tracking-wider text-white">
              Phoenix<span className="text-orange-500">Billing</span>
            </span>
          </Link>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-xs">
            The next-generation billing and automation platform for hosting providers.
          </p>
        </div>

        {/* Product links */}
        <div>
          <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
            Product
          </h4>
          <ul className="space-y-2.5">
            <li>
              <a
                href="#features"
                className="text-sm text-slate-400 hover:text-orange-400 transition-colors duration-200"
              >
                Features
              </a>
            </li>
            <li>
              <a
                href="#pricing"
                className="text-sm text-slate-400 hover:text-orange-400 transition-colors duration-200"
              >
                Pricing
              </a>
            </li>
          </ul>
        </div>

        {/* Resources links */}
        <div>
          <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
            Resources
          </h4>
          <ul className="space-y-2.5">
            <li>
              <Link
                to="/knowledge-base"
                className="text-sm text-slate-400 hover:text-orange-400 transition-colors duration-200"
              >
                Knowledge Base
              </Link>
            </li>
            <li>
              <Link
                to="/network-status"
                className="text-sm text-slate-400 hover:text-orange-400 transition-colors duration-200"
              >
                Network Status
              </Link>
            </li>
          </ul>
        </div>

        {/* Account links */}
        <div>
          <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
            Account
          </h4>
          <ul className="space-y-2.5">
            <li>
              <Link
                to="/login"
                className="text-sm text-slate-400 hover:text-orange-400 transition-colors duration-200"
              >
                Login
              </Link>
            </li>
            <li>
              <Link
                to="/register"
                className="text-sm text-slate-400 hover:text-orange-400 transition-colors duration-200"
              >
                Register
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-slate-500">
          &copy; {new Date().getFullYear()} PhoenixBilling. All rights reserved.
        </p>
        <div className="flex items-center gap-4">
          <span className="text-xs text-slate-600">
            Powered by{' '}
            <span className="text-orange-500/70 font-medium">Phoenix</span>
          </span>
        </div>
      </div>
    </div>
  </footer>
);

/* ------------------------------------------------------------------ */
/*  Main Page Component                                                */
/* ------------------------------------------------------------------ */

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-900 text-white selection:bg-orange-500/30 selection:text-orange-100">
      <Navbar />
      <Hero />
      <Features />
      <Products />
      <CtaBanner />
      <Footer />
    </div>
  );
};

export default LandingPage;
